import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useI18n } from '../i18n/index.tsx';
import type { MessageKey } from '../i18n/messages.ts';
import { api } from '../api/client.ts';
import { useApiError } from '../api/describe.ts';
import type {
  CompatibilityEntry,
  Finding,
  PanelCatalogEntry,
  PlanView,
  Server,
  TopologyPresetEntry,
  TunnelCatalogEntry,
} from '../api/types.ts';
import { Button, Card, Notice, Skeleton } from '../components/ui.tsx';
import {
  INITIAL_STATE,
  WIZARD_STEPS,
  cleanAssignments,
  validateStep,
  type WizardState,
  type WizardStep,
} from './state.ts';
import { PanelStep } from './PanelStep.tsx';
import { ShapeStep } from './ShapeStep.tsx';
import { ServersStep } from './ServersStep.tsx';
import { OptionsStep } from './OptionsStep.tsx';
import { ReviewStep } from './ReviewStep.tsx';
import './Wizard.css';

/**
 * The seven-step flow from "I want a panel" to "here are your details".
 *
 * Five steps happen here; the sixth and seventh — watching it install and
 * collecting credentials — are the deployment screen, because a customer who
 * closes the tab must be able to come back to them by URL.
 *
 * Nothing in this flow touches a server. The last step asks the API to *plan*,
 * which reads what we already know and returns something to read; a separate,
 * deliberate approval is what starts any work.
 */

const STEP_LABEL: Record<WizardStep, MessageKey> = {
  panel: 'wizard.step.panel',
  shape: 'wizard.step.shape',
  servers: 'wizard.step.servers',
  options: 'wizard.step.options',
  review: 'wizard.step.review',
};

interface Catalog {
  readonly panels: readonly PanelCatalogEntry[];
  readonly tunnels: readonly TunnelCatalogEntry[];
  readonly presets: readonly TopologyPresetEntry[];
  readonly compatibility: readonly CompatibilityEntry[];
}

export function Wizard({
  onStarted,
  onAddServer,
}: {
  onStarted: (deploymentId: string) => void;
  onAddServer: () => void;
}): ReactNode {
  const { t } = useI18n();
  const describe = useApiError();
  const [catalog, setCatalog] = useState<Catalog | undefined>();
  const [servers, setServers] = useState<readonly Server[] | undefined>();
  const [state, setState] = useState<WizardState>(INITIAL_STATE);
  const [step, setStep] = useState<WizardStep>('panel');
  const [plan, setPlan] = useState<
    { id: string; plan: PlanView; findings: readonly Finding[] } | undefined
  >();
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const [panels, tunnels, presets, compatibility, serverList] = await Promise.all([
          api.panels(),
          api.tunnels(),
          api.topologies(),
          api.compatibility(),
          api.servers(),
        ]);
        setCatalog({ panels, tunnels, presets, compatibility });
        setServers(serverList);
      } catch {
        setError(t('common.somethingWentWrong'));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const context = useMemo(
    () => ({
      panels: catalog?.panels ?? [],
      presets: catalog?.presets ?? [],
      servers: servers ?? [],
    }),
    [catalog, servers],
  );

  const validity = validateStep(step, state, context);
  const index = WIZARD_STEPS.indexOf(step);

  const goTo = (next: WizardStep): void => {
    setError(undefined);
    setStep(next);
  };

  const next = async (): Promise<void> => {
    if (!validity.complete) return;
    if (step !== 'options') {
      goTo(WIZARD_STEPS[index + 1] as WizardStep);
      return;
    }

    // Leaving the options step is where the plan is produced. Still read-only:
    // the API reads stored inventories and returns something to look at.
    setBusy(true);
    setError(undefined);
    try {
      const result = await api.createDeployment({
        panelAdapterId: state.panelAdapterId as string,
        preset: state.preset as string,
        assignments: cleanAssignments(state),
        tunnel: state.tunnel,
        domain: state.domain.trim() || undefined,
        requestSsl: state.requestSsl,
        panelPort: state.panelPort ? Number(state.panelPort) : undefined,
        requireEncryptedTunnel: state.requireEncryptedTunnel,
      });
      setPlan({ id: result.deployment.id, plan: result.plan, findings: result.findings });
      goTo('review');
    } catch (caught) {
      setError(describe(caught));
    } finally {
      setBusy(false);
    }
  };

  const approve = async (): Promise<void> => {
    if (!plan) return;
    setBusy(true);
    setError(undefined);
    try {
      await api.approveDeployment(plan.id, plan.plan.hash);
      onStarted(plan.id);
    } catch (caught) {
      setError(describe(caught));
      setBusy(false);
    }
  };

  if (!catalog || !servers) {
    return (
      <Card>
        <Skeleton lines={6} />
      </Card>
    );
  }

  return (
    <>
      <header className="page-header">
        <div>
          <h1>{t('wizard.title')}</h1>
          <p className="muted">{t('wizard.step', { current: index + 1, total: 7 })}</p>
        </div>
        <ModeToggle mode={state.mode} onChange={(mode) => setState({ ...state, mode })} />
      </header>

      <Stepper current={step} state={state} context={context} onSelect={goTo} />

      <Card>
        {step === 'panel' ? (
          <PanelStep
            panels={catalog.panels}
            compatibility={catalog.compatibility}
            state={state}
            onChange={setState}
          />
        ) : null}
        {step === 'shape' ? (
          <ShapeStep
            presets={catalog.presets}
            panels={catalog.panels}
            compatibility={catalog.compatibility}
            servers={servers}
            state={state}
            onChange={setState}
            onAddServer={onAddServer}
          />
        ) : null}
        {step === 'servers' ? (
          <ServersStep
            presets={catalog.presets}
            servers={servers}
            state={state}
            onChange={setState}
            onAddServer={onAddServer}
          />
        ) : null}
        {step === 'options' ? (
          <OptionsStep
            tunnels={catalog.tunnels}
            presets={catalog.presets}
            state={state}
            onChange={setState}
          />
        ) : null}
        {step === 'review' && plan ? (
          <ReviewStep plan={plan.plan} findings={plan.findings} />
        ) : null}

        {validity.reason ? (
          <Notice tone="warning">
            {t(validity.reason.key as MessageKey, validity.reason.values)}
          </Notice>
        ) : null}
        {error ? <Notice tone="danger">{error}</Notice> : null}

        <div className="form-actions wizard__actions">
          {index > 0 ? (
            <Button variant="ghost" onClick={() => goTo(WIZARD_STEPS[index - 1] as WizardStep)}>
              {t('common.back')}
            </Button>
          ) : null}
          {step === 'review' ? (
            <Button
              size="lg"
              busy={busy}
              // A blocker is not something to warn about and continue past.
              disabled={plan?.findings.some((finding) => finding.severity === 'blocker') ?? false}
              onClick={() => void approve()}
            >
              {busy ? t('wizard.review.approving') : t('wizard.review.approve')}
            </Button>
          ) : (
            <Button busy={busy} disabled={!validity.complete} onClick={() => void next()}>
              {t('common.next')}
            </Button>
          )}
        </div>
      </Card>
    </>
  );
}

function Stepper({
  current,
  state,
  context,
  onSelect,
}: {
  current: WizardStep;
  state: WizardState;
  context: Parameters<typeof validateStep>[2];
  onSelect: (step: WizardStep) => void;
}): ReactNode {
  const { t } = useI18n();
  const currentIndex = WIZARD_STEPS.indexOf(current);

  return (
    <ol className="stepper">
      {WIZARD_STEPS.map((step, index) => {
        const done = index < currentIndex && validateStep(step, state, context).complete;
        // Backwards is always allowed; forwards only through the flow, so a
        // half-filled step cannot be skipped past.
        const selectable = index < currentIndex;
        return (
          <li
            key={step}
            className={[
              'stepper__item',
              index === currentIndex ? 'is-current' : '',
              done ? 'is-done' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <button
              type="button"
              disabled={!selectable}
              aria-current={index === currentIndex ? 'step' : undefined}
              onClick={() => onSelect(step)}
            >
              <span className="stepper__number">{done ? '✓' : index + 1}</span>
              <span className="stepper__label">{t(STEP_LABEL[step])}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

/** Easy hides the settings most customers should not have to think about. */
function ModeToggle({
  mode,
  onChange,
}: {
  mode: WizardState['mode'];
  onChange: (mode: WizardState['mode']) => void;
}): ReactNode {
  const { t } = useI18n();
  return (
    <div className="mode-toggle" role="group" aria-label={t('mode.label')}>
      {(['easy', 'expert'] as const).map((value) => (
        <button
          key={value}
          type="button"
          className={mode === value ? 'is-active' : undefined}
          aria-pressed={mode === value}
          onClick={() => onChange(value)}
        >
          {t(value === 'easy' ? 'mode.easy' : 'mode.expert')}
        </button>
      ))}
    </div>
  );
}
